<template>
  <div id="HongKong-map" class="HongKong-map"></div>
</template>

<script>
import Map3d from "@/utils/Map3d.js";
import TWEEN from "@tweenjs/tween.js";
import * as THREE from "three";
import { GUI } from "three/examples/jsm/libs/lil-gui.module.min.js";
import { random } from "@/utils";
import useFileLoader from "@/hooks/useFileLoader.js";
import useCountry from "@/hooks/useCountry.js";
import useCoord from "@/hooks/useCoord.js";
import useConversionStandardData from "@/hooks/useConversionStandardData.js";
import useMapMarkedLightPillar from "@/hooks/map/useMapMarkedLightPillar";
import useSequenceFrameAnimate from "@/hooks/useSequenceFrameAnimate";
import useCSS2DRender from "@/hooks/useCSS2DRender";

let centerXY = [114.1543731, 22.28198083];

export default {
  name: "HongHongMap",
  data() {
    return {
      baseEarth: null,
    };
  },
  async mounted() {
    const { requestData } = useFileLoader();
    const { transfromGeoJSON } = useConversionStandardData();
    const { getBoundingBox } = useCoord();
    const { createCountryFlatLine } = useCountry();
    const { initCSS2DRender, create2DTag } = useCSS2DRender();
    const { createLightPillar } = useMapMarkedLightPillar({
      scaleFactor: 1.2,
    });
    const { createSequenceFrame } = useSequenceFrameAnimate();

    const texture = new THREE.TextureLoader();
    const textureMap = texture.load("models/gz-map.jpg");
    const texturefxMap = texture.load("models/gz-map-fx.jpg");
    const rotatingApertureTexture = texture.load("models/rotatingAperture.png");
    const rotatingPointTexture = texture.load("models/rotating-point2.png");
    const circlePoint = texture.load("models/circle-point.png");
    const sceneBg = texture.load("models/scene-bg2.png");

    textureMap.wrapS = texturefxMap.wrapS = THREE.RepeatWrapping;
    textureMap.wrapT = texturefxMap.wrapT = THREE.RepeatWrapping;
    textureMap.flipY = texturefxMap.flipY = false;
    textureMap.rotation = texturefxMap.rotation = THREE.MathUtils.degToRad(45);
    const scale = 0.128;
    textureMap.repeat.set(scale, scale);
    texturefxMap.repeat.set(scale, scale);

    const topFaceMaterial = new THREE.MeshPhongMaterial({
      map: textureMap,
      color: 0x1abef4,
      combine: THREE.MultiplyOperation,
      transparent: true,
      opacity: 1,
    });

    const sideMaterial = new THREE.MeshLambertMaterial({
      color: 0x2b3632,
      transparent: true,
      opacity: 1,
    });

    const bottomZ = 0;

    const initGui = () => {
      const gui = new GUI();
      const guiParams = {
        topColor: "#1abef4",
        sideColor: "#2b3632",
        scale: 0.1,
      };
      gui.addColor(guiParams, "topColor").onChange((val) => {
        topFaceMaterial.color = new THREE.Color(val);
      });
      gui.addColor(guiParams, "sideColor").onChange((val) => {
        sideMaterial.color = new THREE.Color(val);
      });
      gui.add(guiParams, "scale", 0, 1).onChange((val) => {
        textureMap.repeat.set(val, val);
        texturefxMap.repeat.set(val, val);
      });
    };

    const initRotatingAperture = (scene, width) => {
      let plane = new THREE.PlaneBufferGeometry(width, width);
      let material = new THREE.MeshBasicMaterial({
        map: rotatingApertureTexture,
        transparent: true,
        opacity: 1,
        depthTest: true,
      });
      let mesh = new THREE.Mesh(plane, material);
      mesh.position.set(...centerXY, bottomZ + 0.01);
      mesh.scale.set(1.1, 1.1, 1.1);
      scene.add(mesh);
      return mesh;
    };

    const initRotatingPoint = (scene, width) => {
      let plane = new THREE.PlaneBufferGeometry(width, width);
      let material = new THREE.MeshBasicMaterial({
        map: rotatingPointTexture,
        transparent: true,
        opacity: 1,
        depthTest: true,
      });
      let mesh = new THREE.Mesh(plane, material);
      mesh.position.set(...centerXY, bottomZ + 0.02);
      mesh.scale.set(1.1, 1.1, 1.1);
      scene.add(mesh);
      return mesh;
    };

    const initSceneBg = (scene, width) => {
      let plane = new THREE.PlaneBufferGeometry(width * 4, width * 4);
      let material = new THREE.MeshPhongMaterial({
        color: 0xffffff,
        map: sceneBg,
        transparent: true,
        opacity: 1,
        depthTest: true,
      });

      let mesh = new THREE.Mesh(plane, material);
      mesh.position.set(...centerXY, bottomZ);
      scene.add(mesh);
    };

    const initCirclePoint = (scene, width) => {
      let plane = new THREE.PlaneBufferGeometry(width, width);
      let material = new THREE.MeshPhongMaterial({
        color: 0x00ffff,
        map: circlePoint,
        transparent: true,
        opacity: 1,
      });
      let mesh = new THREE.Mesh(plane, material);
      mesh.position.set(...centerXY, bottomZ - 0.1);
      scene.add(mesh);
    };

    const initParticle = (scene, bound) => {
      let { center, size } = bound;
      let minX = center.x - size.x / 2;
      let maxX = center.x + size.x / 2;
      let minY = center.y - size.y / 2;
      let maxY = center.y + size.y / 2;
      let minZ = -0.5;
      let maxZ = 0.5;

      let particleArr = [];
      for (let i = 0; i < 16; i++) {
        const particle = createSequenceFrame({
          image: "./models/particleEffects.png",
          width: 18,
          height: 19,
          frame: 9,
          column: 9,
          row: 1,
          speed: 0.5,
        });
        let particleScale = random(5, 10) / 1000;
        particle.scale.set(particleScale, particleScale, particleScale);
        particle.rotation.x = Math.PI / 2;
        let x = random(minX, maxX);
        let y = random(minY, maxY);
        let z = random(minZ, maxZ);
        particle.position.set(x, y, z);
        particleArr.push(particle);
      }
      scene.add(...particleArr);
      return particleArr;
    };

    const initBorderLine = (data, mapGroup) => {
      let lineTop = createCountryFlatLine(
        data,
        {
          color: 0xffffff,
          linewidth: 0.0015,
          transparent: true,
          depthTest: false,
        },
        "Line2"
      );
      lineTop.position.z += 0.02;
      let lineBottom = createCountryFlatLine(
        data,
        {
          color: 0x61fbfd,
          linewidth: 0.002,
          depthTest: false,
        },
        "Line2"
      );
      lineBottom.position.z -= 0;
      mapGroup.add(lineTop);
      mapGroup.add(lineBottom);
    };

    const initLightPoint = (properties, mapGroup) => {
      if (!properties.centroid && !properties.center) return false;
      let heightScaleFactor = 0.4 + random(1, 5) / 5;
      let lightCenter = properties.centroid || properties.center;
      let light = createLightPillar(...lightCenter, heightScaleFactor);
      light.position.z = 0.31;
      mapGroup.add(light);
    };

    const initLabel = (properties, scene) => {
      if (!properties.centroid && !properties.center) return false;
      var label = create2DTag("标签", "map-label");
      scene.add(label);
      let labelCenter = properties.center;
      label.show(properties.name, new THREE.Vector3(...labelCenter, 0.0225));
    };

    // 加载地图数据
    let provinceData = await requestData("models/HongKong.json");
    provinceData = transfromGeoJSON(provinceData);

    class CurrentMap3d extends Map3d {
      constructor(props) {
        super(props);
      }
      initCamera() {
        let { width, height } = this.options;
        let rate = width / height;
        this.camera = new THREE.PerspectiveCamera(45, rate, 0.001, 90000000);
        this.camera.up.set(0, 0, 1);
        this.camera.position.set(
          114.16985310256118,
          21.584576159947034,
          0.5440962010842327
        );
        this.camera.lookAt(...centerXY, 0);
      }
      initModel() {
        try {
          this.mapGroup = new THREE.Group();
          this.css2dRender = initCSS2DRender(this.options, this.container);

          provinceData.features.forEach((elem) => {
            const province = new THREE.Object3D();
            const coordinates = elem.geometry.coordinates;
            const properties = elem.properties;

            coordinates.forEach((multiPolygon) => {
              multiPolygon.forEach((polygon) => {
                const shape = new THREE.Shape();
                for (let i = 0; i < polygon.length; i++) {
                  let [x, y] = polygon[i];
                  if (i === 0) shape.moveTo(x, y);
                  shape.lineTo(x, y);
                }
                const extrudeSettings = {
                  depth: 0.02,
                  bevelEnabled: false,
                  bevelSegments: 1,
                  bevelThickness: 0.1,
                };
                const geometry = new THREE.ExtrudeGeometry(
                  shape,
                  extrudeSettings
                );
                const mesh = new THREE.Mesh(geometry, [
                  topFaceMaterial,
                  sideMaterial,
                ]);
                province.add(mesh);
              });
            });

            this.mapGroup.add(province);
            initLightPoint(properties, this.mapGroup);
            initLabel(properties, this.scene);
          });

          initBorderLine(provinceData, this.mapGroup);

          let earthGroupBound = getBoundingBox(this.mapGroup);
          centerXY = [earthGroupBound.center.x, earthGroupBound.center.y];
          let { size } = earthGroupBound;
          let width = size.x < size.y ? size.y + 0.2 : size.x + 0.2;

          this.rotatingApertureMesh = initRotatingAperture(this.scene, width);
          this.rotatingPointMesh = initRotatingPoint(this.scene, width - 0.1);
          initCirclePoint(this.scene, width);
          initSceneBg(this.scene, width);

          this.scene.add(this.mapGroup);
          this.particleArr = initParticle(this.scene, earthGroupBound);
          // initGui();
        } catch (error) {
          console.log(error);
        }
      }
      initControls() {
        super.initControls();
        this.controls.target = new THREE.Vector3(...centerXY, 0);
      }
      initLight() {
        let directionalLight1 = new THREE.DirectionalLight(0x7af4ff, 1);
        directionalLight1.position.set(...centerXY, 30);
        let directionalLight2 = new THREE.DirectionalLight(0x7af4ff, 1);
        directionalLight2.position.set(...centerXY, 30);
        let ambientLight = new THREE.AmbientLight(0x7af4ff, 1);
        this.addObject(directionalLight1);
        this.addObject(directionalLight2);
        this.addObject(ambientLight);
      }
      loop() {
        this.animationStop = window.requestAnimationFrame(() => {
          this.loop();
        });
        this.renderer.render(this.scene, this.camera);
        if (this.options.controls.visibel && this.controls) {
          this.controls.update();
        }
        if (this.options.statsVisibel) this.stats.update();
        if (this.rotatingApertureMesh) {
          this.rotatingApertureMesh.rotation.z += 0.0005;
        }
        if (this.rotatingPointMesh) {
          this.rotatingPointMesh.rotation.z -= 0.0005;
        }
        if (this.css2dRender) {
          this.css2dRender.render(this.scene, this.camera);
        }
        // 粒子上升
        if (this.particleArr.length) {
          for (let i = 0; i < this.particleArr.length; i++) {
            this.particleArr[i].updateSequenceFrame();
            this.particleArr[i].position.z += 0.002;
            if (this.particleArr[i].position.z >= 0.5) {
              this.particleArr[i].position.z = -0.5;
            }
          }
        }
        TWEEN.update();
      }
      resize() {
        super.resize();
        this.renderer.render(this.scene, this.camera);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        if (this.css2dRender) {
          this.css2dRender.setSize(this.options.width, this.options.height);
        }
      }
    }

    this.baseEarth = new CurrentMap3d({
      container: "#HongKong-map",
      axesVisibel: true,
      controls: {
        enableDamping: true,
        maxPolarAngle: (Math.PI / 2) * 0.98,
      },
    });
    this.baseEarth.run();
    window.addEventListener("resize", this.resize);
    this.$emit("mapReady");
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resize);
  },
  methods: {
    resize() {
      if (this.baseEarth) {
        this.baseEarth.resize();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./style.scss";
</style>